import type { IntegrationStep } from "@/types/integration"
import type { ModelFacade } from "@/utils/useEngineModel/ModelFacade"
import type {
  IntegrationConfig,
  IntegrationImportCompleted } from "@kaleidoscope-solutions/engine-wasm"
import type { UseStepperReturn } from "@vueuse/core"
import Connection from "@/components/Integration/Connection.vue"
import messageHandler from "@/logic/message-handler"
import { IntegrationPhaseSteps } from "@/types/integration"
import { getDecorator } from "@/utils"
import { InteractionMap } from "@/utils/fileImport/interactions"
import { captureError } from "@/utils/useEngineModel/ModelFacade"
import { useSnackbar } from "@/utils/useSnackbar"
import {
  IntegrationTarget,
  MessageKind
} from "@kaleidoscope-solutions/engine-wasm"
import { useStepper } from "@vueuse/core"
import { defineStore } from "pinia"
import { markRaw } from "vue"

const { addSnackbar } = useSnackbar()

export enum Integration {
  Shopify = "Shopify",
}

export type IntegrationType = keyof typeof IntegrationTarget

interface State {
  model?: ModelFacade
  dialog: boolean
  stepInteractions: InteractionMap<IntegrationPhaseSteps>
  integrations: IntegrationConfig[]
  currentIntegration?: IntegrationType
  running: boolean
  firstLoad: boolean
}

export const useIntegrationStore = defineStore("integration", {
  state: (): State => ({
    model: undefined,
    dialog: false,
    stepInteractions: new InteractionMap(),
    integrations: [],
    currentIntegration: undefined,
    running: false,
    firstLoad: true,
  }),
  actions: {
    initialise(model: ModelFacade) {
      this.model = model
      this.setupListeners()
      this.modelApi.integrations()
    },
    setupListeners() {
      messageHandler.on(MessageKind.AppOffline, () => {
        console.error("App Offline")
      })

      // Updating
      messageHandler.on(MessageKind.IntegrationUpdated, async () => {
        await this.modelApi.integrations()
      })

      messageHandler.on(
        MessageKind.IntegrationUpdateFailed,
        (integration: string) => {
          console.error("IntegrationUpdateFailed", integration)
        }
      )

      // Removing
      messageHandler.on(MessageKind.IntegrationRemoved, async () => {
        addSnackbar({
          type: "success",
          message: "Your integration has been removed",
        })
        await this.modelApi.integrations()
      })

      // Getting
      messageHandler.on(MessageKind.IntegrationsList, (integrations) => {
        this.integrations = JSON.parse(integrations) as IntegrationConfig[]

        if (this.firstLoad) {
          this.integrations.forEach((integration) => {
            this.runIntegration(integration.integration)
          })
          this.firstLoad = false
        }
      })

      // Running
      messageHandler.on(
        MessageKind.IntegrationRunCompleted,
        async (jsonData) => {
          await Promise.all([
            this.modelApi.integrations(),
            this.runimport(jsonData),
          ])
        }
      )

      messageHandler.on(
        MessageKind.IntegrationImportCompleted,
        (message: string) => {
          const integration = JSON.parse(message) as IntegrationImportCompleted
          addSnackbar({
            type: "success",
            message: `${integration.integrationTarget} integration is complete. Data has been imported`,
          })
        }
      )

      messageHandler.on(MessageKind.IntegrationRunFailed, (integration) => {
        this.running = false
        addSnackbar({
          type: "error",
          message: `Data could not be imported from ${integration}.`,
        })
      })
    },
    reset() {
      // Reset the stepper to go back to the first step.
      this.stepper.goTo(this.steps[0])
      this.currentIntegration = undefined
    },
    markStepTouched(IntegrationPhaseSteps: IntegrationPhaseSteps) {
      this.stepInteractions.markTouched(IntegrationPhaseSteps)
    },
    goToStep(IntegrationPhaseSteps: IntegrationPhaseSteps) {
      const foundStep = this.steps.find(
        (step) => step.title === IntegrationPhaseSteps
      )
      if (!foundStep) {
        console.warn("Step not found", IntegrationPhaseSteps)
        return
      }

      const previousStep = this.stepper.current.value.title
      this.markStepTouched(previousStep)

      this.stepper.goTo(foundStep)
    },
    setCurrentIntegration(integration: Integration) {
      this.currentIntegration = integration
    },
    isConfigured(integration: Integration) {
      const configuration = this.integrations.find(
        (i) => i.integration === integration
      )

      if (configuration === undefined) {
        return false
      }

      return configuration.token_status === "TokenPresent"
    },
    integrationConfig(integration: Integration) {
      return this.integrations.find((i) => i.integration === integration)
    },
    async updateIntegration(config: IntegrationConfig.Shopify) {
      await this.modelApi.updateIntegration(config)
    },
    async removeIntegration(integration: Integration) {
      await this.modelApi.removeIntegration(IntegrationTarget[integration])
    },
    async removeAllIntegrations() {
      for (let i = 0; i < this.integrations.length; i++) {
        const integration = this.integrations[i]
        await this.modelApi.removeIntegration(
          IntegrationTarget[integration.integration]
        )
      }
    },
    async runIntegration(integration: IntegrationType) {
      this.running = true
      await this.modelApi.runIntegration(IntegrationTarget[integration])
    },
    async runCurrentIntegration() {
      if (this.currentIntegration === undefined) {
        throw new Error("Current integration not set.")
      }
      await this.runIntegration(this.currentIntegration)
    },
    async runimport(jsonData: string) {
      try {
        const data = JSON.parse(jsonData) as any

        if (data.Shopify.length === 0) {
          addSnackbar({
            type: "info",
            message: "No new orders to import",
          })
          return
        }

        const importData = JSON.stringify({
          dataFormat: "shopify_json",
          data,
        })

        await this.model?.save(async () => {
          await this.modelApi.import(importData)
        })
      } catch (error) {
        captureError(error, { snackbar: true })
      } finally {
        this.running = false
      }
    },
  },
  getters: {
    modelApi(state) {
      if (state.model === undefined) {
        throw new Error("Model not initialised")
      }
      return state.model?.api
    },
    currentIntegrationConfig(state): IntegrationConfig | undefined {
      return state.integrations.find(
        (i) => i.integration === state.currentIntegration
      )
    },
    steps(): IntegrationStep[] {
      return [
        {
          title: IntegrationPhaseSteps.CONNECTION,
          component: markRaw(Connection),
          showNext: () => true,
          decorator: () =>
            getDecorator(
              this.stepInteractions.get(IntegrationPhaseSteps.CONNECTION),
              true
            ),
        },
        // TODO: Add back when showing preview before import.
        // {
        //   title: IntegrationPhaseSteps.PREVIEW,
        //   component: markRaw(Preview),
        //   showNext: () => true,
        //   decorator: () => Decorator.NOT_APPLICABLE,
        // },
      ]
    },
    stepper(): UseStepperReturn<any, IntegrationStep[], IntegrationStep> {
      return useStepper<any>(this.steps)
    },
  },
})
